import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { ValidationMessageModel } from '@data-layer/models/common';
import {
  MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING,
  MISSING_ATTACHED_DOCUMENT_INVOICE,
  MISSING_ATTACHED_DOCUMENT_POA,
  MISSING_ATTACHED_DOCUMENT_PUN,
} from '@constants/entity-validation-messages';
import { enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { IEsDocumentStateUpdate } from '../../interfaces';

@Component({
  selector: 'es-document-download',
  templateUrl: './es-document-download.component.html',
  styleUrls: ['./es-document-download.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDocumentDownloadComponent {
  @Input() public document: DocumentModel;
  @Input() public isCanUploadDocuments: boolean;
  @Input() public isCanRemoveDocuments: boolean = false;
  @Input() public validationMessages: ValidationMessageModel[] = [];
  @Input() public attachmentTypeName: string;
  @Input() public attachmentName: string;
  @Input() public attachmentHint: string;
  @Input() public isShowDeferredRequiredTag: boolean = false;
  @Input() public requiredLaterTooltip: string;
  @Input() public isTruck: boolean;
  @Input() public upload: (document: DocumentCreateModel, file: File) => Observable<any>;
  @Input() public remove: (documentId: number) => Observable<any>;
  @Output() public stateUpdate: EventEmitter<IEsDocumentStateUpdate> = new EventEmitter<IEsDocumentStateUpdate>();

  public isLoading: boolean = false;

  constructor() {}

  public get isRequiredDocument(): boolean {
    return !!this.validationMessages.find(
      (message) =>
        ((message.text === MISSING_ATTACHED_DOCUMENT_INVOICE && this.document.type === enumDocumentType.INVOICE) ||
          (message.text === MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING &&
            this.document.type === enumDocumentType.BILL_OF_LADING &&
            !this.isTruck) ||
          (message.text === MISSING_ATTACHED_DOCUMENT_POA && this.document.type === enumDocumentType.POWER_OF_ATTORNEY) ||
          (message.text === MISSING_ATTACHED_DOCUMENT_PUN && this.document.type === enumDocumentType.ACH_APPROVAL)) &&
        message.params &&
        message.params.relatedEntityId === this.document.relatedEntityId,
    );
  }

  public get canRemoveDocuments(): boolean {
    return (
      this.isCanRemoveDocuments ||
      [enumDocumentState.SCAN_FAILED, enumDocumentState.FILE_SIZE_TO_BIG_AFTER_SCAN].includes(this.document.state)
    );
  }

  public onStateUpdate(state: enumDocumentState) {
    this.stateUpdate.emit({ documentId: this.document.id, state });
  }

  public onRemoveFile(event: DocumentModel): void {
    if (!this.canRemoveDocuments || !this.remove) {
      return;
    }
    this.isLoading = true;
    this.remove(event.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        take(1),
      )
      .subscribe();
  }

  public onUploadFile(file: File): void {
    if (!this.upload) {
      return;
    }
    this.isLoading = true;
    this.upload({ ...this.document, fileName: file.name }, file)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        take(1),
      )
      .subscribe();
  }
}
